<template>
	<div class="flex wrap xs-12">
		<j-accordion
			class="xs-12 bb1 bc7"
			:startOpen="true"
			v-bind="accordionProps"
			bodyClass="bg5 p4"
		>
			<template v-slot:title>{{ $t("general") }}</template>
			<template v-slot:body>
				<!-- Name -->
				<core-form-input
					componentName="j-base-input-text"
					name="name"
					id="name"
					:label="$t('template_name')"
					v-model="template.name"
					@blur="updateTemplate"
				/>
				<!-- Description -->
				<core-form-input
					class="mt6"
					componentName="j-base-input-long-text"
					name="description"
					id="description"
					:label="$t('description')"
					v-model="template.description"
					@blur="updateTemplate"
				/>
				<!-- passwordLiquid -->
				<core-form-input
					class="mt6"
					componentName="j-base-input-text"
					name="passwordLiquid"
					id="passwordLiquid"
					:label="$t('passwordLiquid')"
					v-model="template.passwordLiquid"
					@blur="updateTemplate"
				/>
			</template>
		</j-accordion>

		<!-- Graphql Query -->
		<j-accordion
			class="xs-12 bb1 bc7"
			bodyClass="bg5 p4"
			v-bind="accordionProps"
		>
			<template v-slot:title>{{ $t("graphql_query") }}</template>
			<template v-slot:body>
				<core-monaco-editor language="graphql" v-model="computedGraphQlQuery" />
			</template>
		</j-accordion>

		<!-- Select File key -->
		<j-accordion
			v-if="templateType === 'pdf_drawing'"
			class="xs-12 bb1 bc7"
			bodyClass="bg5 p4"
			v-bind="accordionProps"
		>
			<template v-slot:title>{{ $t("select_pdf_file") }}</template>
			<template v-slot:body>
				<j-base-multi-select-with-flexible-value
					id="select_pdf_file"
					:options="fileOptions"
					:placeholder="$t('select_pdf_file')"
					:allowLocalSearch="false"
					:allowMultiSelect="false"
					:isLoading="isLoading"
					valueKey="id"
					nameKey="fileName"
					v-validate="{ required: true }"
					@input="handleInput($event)"
					:value="selectedFile"
				/>
			</template>
		</j-accordion>

		<!-- Spacing -->
		<j-accordion
			v-if="templateType === 'wkhtml_to_pdf'"
			class="xs-12 bb1 bc7"
			bodyClass="flex wrap bg5 p4"
			v-bind="accordionProps"
		>
			<template v-slot:title>{{ $t("spacing") }}</template>
			<template v-slot:body>
				<!-- Margin -->
				<p class="fs3 fw7 mb1">{{ $t("margin") }}</p>
				<div class="bg5 flex xs-12 xs-middle mb3">
					<p class="fs3 mr1">{{ `${$t("top")}:` }}</p>
					<j-base-input-number
						class="mr2 xs-1"
						v-model="template.marginSettings.top"
						@blur="updateTemplate"
					/>
					<p class="fs3 mr1">{{ `${$t("right")}:` }}</p>
					<j-base-input-number
						class="mr2 xs-1"
						v-model="template.marginSettings.right"
						@blur="updateTemplate"
					/>
					<p class="fs3 mr1">{{ `${$t("bottom")}:` }}</p>
					<j-base-input-number
						class="mr2 xs-1"
						v-model="template.marginSettings.bottom"
						@blur="updateTemplate"
					/>
					<p class="fs3 mr1">{{ `${$t("left")}:` }}</p>
					<j-base-input-number
						class="xs-1"
						v-model="template.marginSettings.left"
						@blur="updateTemplate"
					/>
				</div>
				<!-- Header -->
				<p class="fs3 fw7 mb1">{{ `${$t("header")}:` }}</p>
				<div class="bg5 flex xs-12 xs-middle mb3">
					<j-base-input-number
						class="xs-2"
						v-model="template.headerSettings.spacing"
						@blur="updateTemplate"
					/>
				</div>
				<!-- Footer -->
				<p class="fs3 fw7 mb1">{{ `${$t("footer")}:` }}</p>
				<div class="bg5 flex xs-12 xs-middle">
					<j-base-input-number
						class="xs-2"
						v-model="template.footerSettings.spacing"
						@blur="updateTemplate"
					/>
				</div>
			</template>
		</j-accordion>
	</div>
</template>

<script>
import CoreFormInput from "@/modules/core/components/CoreFormInput"
import JBaseInputText from "@/components/JBaseInputText/"
import JBaseInputNumber from "@/components/JBaseInputNumber/"
import JAccordion from "@/components/JAccordion/"
import CoreMonacoEditor from "@/modules/core/components/CoreMonacoEditor"
import JBaseMultiSelectWithFlexibleValue from "@/components/JBaseMultiSelectWithFlexibleValue/"
import api from "../api/"
import debounce from "lodash.debounce"

export default {
	components: {
		CoreFormInput,
		CoreMonacoEditor,
		JBaseInputText,
		JBaseInputNumber,
		JAccordion,
		JBaseMultiSelectWithFlexibleValue
	},
	props: {
		templateType: null, //pdf_drawing || wkhtml_to_pdf
		payload: Object
	},
	data() {
		return {
			template: null,
			accordionProps: {
				colorWhenOpen: "bg1",
				colorWhenClosed: "bg5"
			},
			selectedFile: null,
			fileOptions: []
		}
	},
	methods: {
		async updateTemplate() {
			if (!this.template.name) return

			let apiKey =
				this.templateType === "pdf_drawing"
					? "updatePdfDrawingTemplate"
					: "updateWkhtmltopdfTemplate"

			let input = {
				name: this.template.name,
				description: this.template.description,
				graphQlQuery: this.template.graphQlQuery
			}

			if (this.templateType === "pdf_drawing") {
				input = {
					...input,
					fileKey: this.selectedFile?.[0]?.key,
					passwordLiquid: this.template?.passwordLiquid
				}
			}

			if (this.templateType === "wkhtml_to_pdf") {
				input = {
					...input,
					passwordLiquid: this.template?.passwordLiquid,
					marginSettings: this.template?.marginSettings,
					headerSettings: this.template?.headerSettings,
					footerSettings: this.template?.footerSettings
				}
			}

			const response = await api[apiKey]({
				templateId: this.template.id,
				input: input
			})

			if (response.errors) {
				this.$store.dispatch("addMessage", {
					message: {
						type: "error",
						text: response.errors
					}
				})
				return
			}

			this.$emit("update")

			this.$store.dispatch("addMessage", {
				message: {
					type: "success",
					text: this.$t("template_updated")
				}
			})
		},

		onGraphQlQueryChange: debounce(function() {
			this.updateTemplate()
		}, 500),

		async fetchUploadedPdfTemplates() {
			this.isLoading = true
			const files = await api.listFileTemplates({ prefix: "templates/" })
			this.isLoading = false
			this.fileOptions = files?.data?.fileListing?.objectSummaries.filter(
				item => item.fileName.includes("pdf")
			)
		},

		handleInput(value) {
			this.selectedFile = value
			this.updateTemplate()
		}
	},

	computed: {
		computedGraphQlQuery: {
			get: function() {
				return this.template.graphQlQuery
			},
			set: function(newValue) {
				this.template.graphQlQuery = newValue
				this.onGraphQlQueryChange()
			}
		}
	},

	async created() {
		await this.fetchUploadedPdfTemplates()
		this.template = this.payload
		this.selectedFile = this.fileOptions?.filter(
			item => item.key === this.template?.pdfUrl
		)
	}
}
</script>
